"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const trustFactors = [{
  value: "24/7",
  label: "OPERATIONAL SUPPORT"
}, {
  value: "15+",
  label: "REGIONAL PORTS SERVED"
}, {
  value: "99%",
  label: "PROCUREMENT ACCURACY"
}];
export default function IndustryTrustFactors() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="industry-trust-factors" className="bg-secondary py-12 md:py-16 border-y border-border/10">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0 divide-y md:divide-y-0 md:divide-x divide-border/20 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {trustFactors.map((factor, i) => <div key={i} data-index={i} className="flex flex-col items-center justify-center py-6 md:py-2 text-center">
              <span className="font-serif font-bold text-5xl md:text-6xl text-primary mb-3 tracking-tight">
                {factor.value}
              </span>
              <span className="font-mono text-sm md:text-base text-secondary-foreground/80 tracking-widest uppercase">
                {factor.label}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}